import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router";
import { Suspense, lazy } from "react";

import { ThemeProvider } from "@/components/theme-provider";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Spinner } from "@/components/ui/spinner";
import { useRenderPerformance } from "@/hooks/use-performance";
import { createOptimizedQueryClient } from "@/lib/query-optimizations";

import { DashboardLayout } from "./components/DashboardLayout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ProtectedWebshopRoute } from "./components/webshop/ProtectedWebshopRoute";
import { SkipToContent } from "./components/SkipToContent";
import { AuthProvider } from "./contexts/AuthContext";
import { WebshopFilterProvider } from "./contexts/WebshopFilterContext";
import { CartProvider } from "./contexts/CartContext";

// Eager load critical pages
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";

// Lazy load other pages for better performance
const PerformanceDashboard = lazy(() => import("@/components/PerformanceDashboard").then(m => ({ default: m.PerformanceDashboard })));
const PerformanceMonitor = lazy(() => import("./pages/PerformanceMonitor"));
const PCKasseMonitor = lazy(() => import("./pages/PCKasseMonitor"));
const ActivityLog = lazy(() => import("./pages/ActivityLog"));
const BulkOperations = lazy(() => import("./pages/BulkOperations"));
const CreateDiscount = lazy(() => import("./pages/CreateDiscount"));
const CreditApplications = lazy(() => import("./pages/CreditApplications"));
const Customers = lazy(() => import("./pages/Customers"));
const CustomerProfile = lazy(() => import("./pages/CustomerProfile"));
const Discounts = lazy(() => import("./pages/Discounts"));
const MigrateCustomer = lazy(() => import("./pages/MigrateCustomer"));
const MyCustomers = lazy(() => import("./pages/MyCustomers"));
const MyUsers = lazy(() => import("./pages/MyUsers"));
const OrderDetails = lazy(() => import("./pages/OrderDetails"));
const Orders = lazy(() => import("./pages/Orders"));
const Partners = lazy(() => import("./pages/Partners"));
const ProductDetails = lazy(() => import("./pages/ProductDetails"));
const Products = lazy(() => import("./pages/Products"));
const Warehouses = lazy(() => import("./pages/Warehouses"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(() => import("./pages/Settings"));
const UserAccess = lazy(() => import("./pages/UserAccess"));
const Users = lazy(() => import("./pages/Users"));
const WebhookSettings = lazy(() => import("./pages/WebhookSettings"));
const WebshopPublic = lazy(() => import("./pages/WebshopPublic"));
const Webshops = lazy(() => import("./pages/Webshops"));
const WebshopSetup = lazy(() => import("./pages/WebshopSetup"));

// Webshop template components
const MinimalistProductPage = lazy(() => import("./components/webshop/templates/minimalist/MinimalistProductPage"));
const MinimalistCategoryPage = lazy(() => import("./components/webshop/templates/minimalist/MinimalistCategoryPage"));
const MinimalistBrandsPage = lazy(() => import("./components/webshop/templates/minimalist/MinimalistBrandsPage"));
const MinimalistBrandProductsPage = lazy(() => import("./components/webshop/templates/minimalist/MinimalistBrandProductsPage"));
const MinimalistCart = lazy(() => import("./components/webshop/templates/minimalist/MinimalistCart"));
const MinimalistCheckout = lazy(() => import("./components/webshop/templates/minimalist/MinimalistCheckout"));
const MinimalistLogin = lazy(() => import("./components/webshop/templates/minimalist/MinimalistLogin"));
const MinimalistForgotPassword = lazy(() => import("./components/webshop/templates/minimalist/MinimalistForgotPassword"));
const MinimalistResetPassword = lazy(() => import("./components/webshop/templates/minimalist/MinimalistResetPassword"));
const MinimalistAccount = lazy(() => import("./components/webshop/templates/minimalist/MinimalistAccount"));
const MinimalistOrders = lazy(() => import("./components/webshop/templates/minimalist/MinimalistOrders"));
const MinimalistOrderDetail = lazy(() => import("./components/webshop/templates/minimalist/MinimalistOrderDetail"));
const MinimalistOrderConfirmation = lazy(() => import("./components/webshop/templates/minimalist/MinimalistOrderConfirmation"));
const MinimalistWishlist = lazy(() => import("./components/webshop/templates/minimalist/MinimalistWishlist"));
const MinimalistComparison = lazy(() => import("./components/webshop/templates/minimalist/MinimalistComparison"));
const TermsOfService = lazy(() => import("./components/webshop/TermsOfService").then(m => ({ default: m.TermsOfService })));

// Loading fallback component
const PageLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-4">
      <Spinner className="size-8" />
      <div className="text-foreground">Laster...</div>
    </div>
  </div>
);

const queryClient = createOptimizedQueryClient();

function App() {
  useRenderPerformance('App');
  
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="system" storageKey="pckasse-theme">
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <SkipToContent />
              <AuthProvider>
                <WebshopFilterProvider>
                  <CartProvider>
                    <Routes>
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/shop/:slug" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <WebshopPublic />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/category/:categoryId" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MinimalistCategoryPage />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/brands" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MinimalistBrandsPage />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/brand/:brandId" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MinimalistBrandProductsPage />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/product/:productId" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MinimalistProductPage />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/cart" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MinimalistCart />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/checkout" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MinimalistCheckout />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/order-confirmation" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MinimalistOrderConfirmation />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/login" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MinimalistLogin />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/forgot-password" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MinimalistForgotPassword />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/reset-password" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MinimalistResetPassword />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/account" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <ProtectedWebshopRoute>
                              <MinimalistAccount />
                            </ProtectedWebshopRoute>
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/orders" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <ProtectedWebshopRoute>
                              <MinimalistOrders />
                            </ProtectedWebshopRoute>
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/orders/:orderId" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <ProtectedWebshopRoute>
                              <MinimalistOrderDetail />
                            </ProtectedWebshopRoute>
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/wishlist" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MinimalistWishlist />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/comparison" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MinimalistComparison />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route path="/shop/:slug/salgsbetingelser" element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <TermsOfService />
                          </Suspense>
                        </ErrorBoundary>
                      } />
                      <Route
                        path="/*"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <ErrorBoundary>
                                <Suspense fallback={<PageLoadingFallback />}>
                                  <Routes>
                                    <Route path="/" element={<Dashboard />} />
                                    <Route path="/webshops" element={<Webshops />} />
                                    <Route path="/webshops/new" element={<WebshopSetup />} />
                                    <Route path="/webshops/:id/edit" element={<WebshopSetup />} />
                                    <Route path="/products" element={<Products />} />
                                    <Route path="/products/:id" element={<ProductDetails />} />
                                    <Route path="/warehouses" element={<Warehouses />} />
                                    <Route path="/orders" element={<Orders />} />
                                    <Route path="/orders/:id" element={<OrderDetails />} />
                                    <Route path="/customers" element={<Customers />} />
                                    <Route path="/credit-applications" element={<CreditApplications />} />
                                    <Route path="/activity-log" element={<ActivityLog />} />
                                    <Route path="/pckasse-monitor" element={<PCKasseMonitor />} />
                                    <Route path="/bulk-operations" element={<BulkOperations />} />
                                    <Route path="/webhook-settings" element={<WebhookSettings />} />
                                    <Route path="/users" element={<Users />} />
                                    <Route path="/my-users" element={<MyUsers />} />
                                    <Route path="/my-customers" element={<MyCustomers />} />
                                    <Route path="/customer-profile/:customerId" element={<CustomerProfile />} />
                                    <Route path="/migrate-customer" element={<MigrateCustomer />} />
                                    <Route path="/partners" element={<Partners />} />
                                    <Route path="/user-access" element={<UserAccess />} />
                                    <Route path="/discounts" element={<Discounts />} />
                                    <Route path="/discounts/create" element={<CreateDiscount />} />
                                    <Route path="/reports" element={<Reports />} />
                                    <Route path="/performance-monitor" element={<PerformanceMonitor />} />
                                    <Route path="/settings" element={<Settings />} />
                                    <Route path="*" element={<NotFound />} />
                                  </Routes>
                                </Suspense>
                              </ErrorBoundary>
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </CartProvider>
                </WebshopFilterProvider>
              </AuthProvider>
            </BrowserRouter>
            <Suspense fallback={null}>
              <PerformanceDashboard />
            </Suspense>
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
